import { createAbortController, abortSignal, requestWasAborted, abortRequest } from "../utils/browserCompat";
import React, { useEffect, useMemo, useState } from "react";
import { Alert, Box, Card, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { listProductCategories } from "../api/products";
import { useAuth } from "../context/AuthContext";
import { useLanding } from "../context/LandingContext";
import { useProjectConfig } from "../context/ProjectConfigContext";
import { handleLandingMediaError, resolveLandingMedia } from "../utils/landingMedia";
import { appendConfiguredQuery, openConfiguredUrl } from "../utils/configuredNavigation";
import { safeTextSx, safeTitleSx, sectionTitleFontSize, textLength } from "../utils/textSafety";
import { scaleResponsiveCss, segmentVisualScale } from "../utils/segmentVisualStyle";
import { capitalizeSectionTitle } from "../utils/landingSectionText";
import {
  catalogListPath,
  normalizeCatalogMode,
  resolveCatalogModeFromSummary,
} from "../utils/catalogRouting";
import { isCanonicalGastronomicCatalogNavigation } from "../utils/siteExperienceDefaults";

function normalize(value) {
  return String(value || "").trim();
}

export default function PromotionsCarousel4({ sectionBackgroundSx = {} }) {
  const { pricingRevision } = useAuth();
  const navigate = useNavigate();
  const { catalog, theme, navigation } = useLanding();
  const { gastronomicMenuEnabled } = useProjectConfig();
  const copy = catalog?.brands || {};
  const categoryCopy = catalog?.categories || {};
  const configuredItems = useMemo(
    () => (Array.isArray(copy?.items) ? copy.items : []).filter((item) =>
      normalize(item?.name ?? item?.label) || resolveLandingMedia(item?.logoUrl ?? item?.imageUrl),
    ),
    [copy?.items],
  );
  const hasConfiguredBrands = configuredItems.length > 0;
  const [loading, setLoading] = useState(!hasConfiguredBrands);
  const [hasLoadedOnce, setHasLoadedOnce] = useState(hasConfiguredBrands);
  const [error, setError] = useState(null);
  const [facetData, setFacetData] = useState({ brands: [], summary: {} });

  useEffect(() => {
    if (hasConfiguredBrands) {
      setLoading(false);
      setHasLoadedOnce(true);
      setError(null);
      setFacetData({ brands: [], summary: {} });
      return undefined;
    }

    const controller = createAbortController();
    const configuredMode = normalizeCatalogMode(catalog?.mode);
    const requestedType = configuredMode === "products"
      ? "product"
      : configuredMode === "services"
        ? "service"
        : "";

    setLoading(true);
    setError(null);
    listProductCategories({ tipo: requestedType, signal: abortSignal(controller) })
      .then((data) => {
        if (!requestWasAborted(controller)) setFacetData(data || { brands: [], summary: {} });
      })
      .catch((requestError) => {
        if (!requestWasAborted(controller)) {
          setError(String(copy.errorText ?? requestError?.message ?? "No se pudieron cargar las marcas."));
        }
      })
      .finally(() => {
        if (!requestWasAborted(controller)) {
          setLoading(false);
          setHasLoadedOnce(true);
        }
      });

    return () => abortRequest(controller);
  }, [catalog?.mode, copy.errorText, hasConfiguredBrands, pricingRevision]);

  const configuredMode = normalizeCatalogMode(catalog?.mode);
  const resolvedMode = hasConfiguredBrands
    ? (configuredMode === "auto" ? "mixed" : configuredMode)
    : resolveCatalogModeFromSummary(catalog?.mode, facetData?.summary);
  const brands = useMemo(() => {
    const map = new Map();
    configuredItems.forEach((configured, index) => {
      const label = normalize(configured?.name ?? configured?.label);
      const query = normalize(configured?.query ?? label);
      const logoUrl = resolveLandingMedia(configured?.logoUrl ?? configured?.imageUrl);
      if (!label && !logoUrl) return;
      const key = label ? `label:${label.toLocaleLowerCase()}` : `configured:${index}`;
      map.set(key, {
        label,
        query,
        logoUrl,
        alt: normalize(configured?.alt ?? label),
        count: Number(configured?.count) || 0,
        sourceIndex: index,
      });
    });

    if (!hasConfiguredBrands) {
      (Array.isArray(facetData?.brands) ? facetData.brands : []).forEach((brand) => {
        const label = normalize(brand?.name ?? brand?.label ?? brand?.key);
        if (!label) return;
        const key = `label:${label.toLocaleLowerCase()}`;
        map.set(key, {
          label,
          query: label,
          logoUrl: "",
          alt: label,
          count: Number(brand?.count) || 0,
        });
      });
    }

    return Array.from(map.values()).sort((a, b) => {
      if (Boolean(a.logoUrl) !== Boolean(b.logoUrl)) return a.logoUrl ? -1 : 1;
      if (b.count !== a.count) return b.count - a.count;
      return a.label.localeCompare(b.label, "es");
    });
  }, [configuredItems, facetData?.brands, hasConfiguredBrands]);

  const repeated = brands.length > 2 ? [...brands, ...brands] : brands;
  const titleScale = segmentVisualScale(theme, "brands", "title");
  const imageScale = segmentVisualScale(theme, "brands", "image");
  const spacingScale = segmentVisualScale(theme, "brands", "spacing");
  const titleSize = scaleResponsiveCss(
    sectionTitleFontSize(copy.title, { xs: "2.15rem", md: "3rem" }),
    titleScale
  );
  const hasHeading = Boolean(copy.eyebrow || copy.title || copy.subtitle);
  const emptyText = String(copy.emptyText ?? "").trim();
  const initialLoading = loading && !hasLoadedOnce;
  const visibleError = error !== null && brands.length === 0;
  if (!initialLoading && visibleError && !error && !hasHeading) return null;
  if (!initialLoading && !visibleError && brands.length === 0 && !hasHeading && !emptyText) return null;

  if (initialLoading) return null;

  const openBrand = (brand) => {
    const search = brand.query || brand.label;
    if (!search) return;
    const configuredCatalogTarget = isCanonicalGastronomicCatalogNavigation(navigation, gastronomicMenuEnabled)
      ? "/productos"
      : navigation?.productsUrl || "/productos";
    openConfiguredUrl(
      appendConfiguredQuery(
        catalogListPath(resolvedMode, configuredCatalogTarget),
        "search",
        search,
      ),
      navigate
    );
  };

  return (
    <Box sx={{ px: { xs: 2, sm: 3, md: 6 }, py: { xs: 5 * spacingScale, sm: 6 * spacingScale, md: 8 * spacingScale }, background: "#FFFFFF", overflow: "hidden", ...sectionBackgroundSx }}>
      <Box sx={{ maxWidth: 1440, mx: "auto" }}>
        {hasHeading ? (
          <Box sx={{ mb: 3.5 }}>
            {copy.eyebrow ? <Typography data-landing-field="catalog.brands.eyebrow" variant="overline" sx={{ ...safeTextSx, color: "#F35A3A" }}>{copy.eyebrow}</Typography> : null}
            {copy.title ? <Typography data-landing-field="catalog.brands.title" variant="h3" sx={{ ...safeTitleSx, fontSize: titleSize, fontWeight: "var(--wp-segment-title-weight)", letterSpacing: "var(--wp-segment-letter-spacing)", color: "#19242F", mb: copy.subtitle ? 1 : 0 }}>{capitalizeSectionTitle(copy.title)}</Typography> : null}
            {copy.subtitle ? <Typography data-landing-field="catalog.brands.subtitle" sx={{ ...safeTextSx, color: "#5B5D73", maxWidth: 760, lineHeight: 1.7, whiteSpace: "pre-line" }}>{copy.subtitle}</Typography> : null}
          </Box>
        ) : null}

        {visibleError ? (
          error ? <Alert data-landing-field="catalog.brands.errorText" severity="warning">{error}</Alert> : null
        ) : brands.length === 0 ? (
          emptyText ? <Alert data-landing-field="catalog.brands.emptyText" severity="info">{emptyText}</Alert> : null
        ) : (
          <Box
            sx={{
              position: "relative",
              overflowX: { xs: "auto", sm: "hidden" },
              overflowY: "hidden",
              py: 1.5,
              mx: { xs: -2, sm: -3, md: -6 },
              px: { xs: 2, sm: 0 },
              scrollSnapType: { xs: "x proximity", sm: "none" },
              overscrollBehaviorX: "contain",
              WebkitOverflowScrolling: "touch",
              maskImage: { xs: "none", sm: "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)" },
              WebkitMaskImage: { xs: "none", sm: "linear-gradient(90deg, transparent 0%, black 8%, black 92%, transparent 100%)" },
              "@keyframes brandMarquee": { "0%": { transform: "translate3d(0,0,0)" }, "100%": { transform: "translate3d(-50%,0,0)" } },
              "&:hover .brand-track": { animationPlayState: "paused" },
            }}
          >
            <Box
              className="brand-track"
              sx={{ display: "flex", width: "max-content", animation: { xs: "none", sm: brands.length > 2 ? "brandMarquee 34s linear infinite" : "none" }, willChange: { xs: "auto", sm: "transform" } }}
            >
              {repeated.map((brand, index) => (
                <Box key={`${brand.label}-${index}`} sx={{ flex: "0 0 auto", width: { xs: 170, sm: 210, md: 235 }, px: .8, display: { xs: index < brands.length ? "block" : "none", sm: "block" }, scrollSnapAlign: { xs: "start", sm: "none" } }}>
                  <Card
                    component={brand.query || brand.label ? "button" : "div"}
                    type={brand.query || brand.label ? "button" : undefined}
                    onClick={brand.query || brand.label ? () => openBrand(brand) : undefined}
                    aria-label={brand.query || brand.label ? `${String(copy.ctaLabel || "Abrir marca").trim()}: ${brand.label || brand.alt || "marca"}` : undefined}
                    sx={{
                      width: "100%",
                      height: { xs: 108 * imageScale, sm: 140 * imageScale, md: 155 * imageScale },
                      border: 0,
                      bgcolor: "#fff",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      p: 2.5,
                      cursor: brand.query || brand.label ? "pointer" : "default",
                      transition: "transform .25s ease, box-shadow .25s ease",
                      "&:hover": { transform: "translateY(-4px)", boxShadow: "0 14px 32px rgba(30,32,52,.18)" },
                    }}
                  >
                    {brand.logoUrl ? (
                      <Box
                        component="img"
                        src={brand.logoUrl}
                        alt={brand.alt}
                        loading="lazy"
                        decoding="async"
                        onError={handleLandingMediaError}
                        sx={{ width: "100%", height: "100%", objectFit: "contain", mixBlendMode: "multiply" }}
                      />
                    ) : (
                      <Box sx={{ minWidth: 0, maxWidth: "100%", textAlign: "center" }}>
                        <Typography
                          data-landing-field={Number.isInteger(brand.sourceIndex) ? `catalog.brands.items.${brand.sourceIndex}.name` : undefined}
                          variant="h4"
                          title={brand.label}
                          sx={{
                            ...safeTextSx,
                            fontSize: textLength(brand.label) > 38
                              ? { xs: "1.25rem", sm: "1.45rem", md: "1.6rem" }
                              : undefined,
                            color: "#19242F",
                            display: "-webkit-box",
                            WebkitBoxOrient: "vertical",
                            WebkitLineClamp: 3,
                            overflow: "hidden",
                          }}
                        >
                          {brand.label}
                        </Typography>
                        {brand.count > 0 ? (
                          <Typography variant="caption" sx={{ color: "#5B5D73" }}>
                            {brand.count} {resolvedMode === "services"
                              ? `servicio${brand.count === 1 ? "" : "s"}`
                              : brand.count === 1
                                ? (categoryCopy.productSingular || "producto")
                                : (categoryCopy.productPlural || "productos")}
                          </Typography>
                        ) : null}
                      </Box>
                    )}
                  </Card>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}
